/**
 * Resize an image (data URL) to a max width for token/API optimization.
 * Keeps aspect ratio; returns WebP or PNG data URL.
 */

const DEFAULT_MAX_WIDTH = 1024;
const WEBP_QUALITY = 0.85;

export interface ResizeOptions {
  maxWidth?: number;
  format?: "image/png" | "image/webp";
  quality?: number;
}

/**
 * Load data URL into an image, draw to canvas at max width, return new data URL.
 */
export function resizeImageForApi(
  dataUrl: string,
  options: ResizeOptions = {}
): Promise<string> {
  const {
    maxWidth = DEFAULT_MAX_WIDTH,
    format = "image/webp",
    quality = WEBP_QUALITY,
  } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = img.naturalWidth;
      const h = img.naturalHeight;
      if (w <= maxWidth) {
        resolve(dataUrl);
        return;
      }
      const scale = maxWidth / w;
      const nw = maxWidth;
      const nh = Math.round(h * scale);
      const canvas = document.createElement("canvas");
      canvas.width = nw;
      canvas.height = nh;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, nw, nh);
      const out = format === "image/webp"
        ? canvas.toDataURL("image/webp", quality)
        : canvas.toDataURL("image/png");
      resolve(out);
    };
    img.onerror = () => reject(new Error("Failed to load image for resize"));
    img.src = dataUrl;
  });
}
